import React, { useState, useEffect } from "react";
import "./TesteCaixa.css";
import { API_ENDPOINTS } from "../config/api";
import { authenticatedFetch } from "../config/authenticatedFetch";

interface Registration {
  id: string;
  external_id: string;
  name: string;
  field_1: string;  // Campo genérico 1
  field_2: string;  // Campo genérico 2
  field_3: string;  // Campo genérico 3
  photo_url: string;
  created_at: string;
  updated_at: string;
}

interface GetAllRegistrationsResponse {
  success: boolean;
  count: number;
  data: Registration[];
}

interface CheckinResponse {
  success: boolean;
  message: string;
  data?: any;
}

const TesteCaixa: React.FC = () => {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [selectedExternalId, setSelectedExternalId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [csrfToken, setCsrfToken] = useState<string>("");

  // Função para buscar o token CSRF
  const fetchCsrfToken = async (): Promise<string> => {
    try {
      
      const response = await authenticatedFetch(API_ENDPOINTS.API_CSRF_TOKEN, {
        method: 'GET',
        credentials: 'include', // Importante para cookies de sessão
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        const token = data.csrfToken; // Mudado de csrf_token para csrfToken
        
        
        setCsrfToken(token);
        return token;
      } else {
        throw new Error(`Erro ao buscar CSRF token: ${response.status}`);
      }
    } catch (err) {
      console.error("Erro ao buscar CSRF token:", err);
      throw err;
    }
  };

  // Carregar todas as registrations e CSRF token ao montar o componente
  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        const response = await authenticatedFetch(API_ENDPOINTS.GET_ALL_REGISTRATIONS, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data: GetAllRegistrationsResponse = await response.json();
          if (data.success) {
            setRegistrations(data.data);
            if (data.data.length > 0) {
              setSelectedExternalId(data.data[0].external_id);
            }
          } else {
            setError("Erro ao carregar registrations");
          }
        } else {
          setError(`Erro HTTP: ${response.status}`);
        }
      } catch (err) {
        setError("Erro de conexão ao carregar registrations");
      }
    };

    const initializeComponent = async () => {
      try {
        // Buscar token CSRF primeiro
        await fetchCsrfToken();
        // Depois buscar registrations
        await fetchRegistrations();
      } catch (err) {
        setError("Erro ao inicializar componente");
      } finally {
        setLoading(false);
      }
    };

    initializeComponent();
  }, []);

  const handleSubmit = async () => {
    if (!selectedExternalId) {
      setResult("❌ Nenhum participante selecionado");
      return;
    }

    setSubmitting(true);
    setResult(null);

    try {
      // Buscar token CSRF atualizado antes do request
      const token = await fetchCsrfToken();
      
      
      
      

      const response = await authenticatedFetch(API_ENDPOINTS.API_CHECKIN, {
        method: 'POST',
        credentials: 'include', // Importante para cookies de sessão
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': token || csrfToken, // Incluir token CSRF no header
        },
        body: JSON.stringify({
          idParticipante: selectedExternalId,
          tipo_checkin: "caixa"
        }),
      });

      const data: CheckinResponse = await response.json();

      if (response.ok && data.success) {
        setResult("✅ Check-in realizado com sucesso!");
      } else {
        setResult(`❌ Erro: ${data.message || "Falha no check-in"}`);
      }
    } catch (err) {
      setResult("❌ Erro de conexão durante check-in");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePainelCheckin = async () => {
    if (!selectedExternalId) {
      setResult("❌ Nenhum participante selecionado");
      return;
    }

    setSubmitting(true);
    setResult(null);

    try {
      // Buscar token CSRF atualizado antes do request
      const token = await fetchCsrfToken();
      

      const response = await authenticatedFetch(API_ENDPOINTS.API_CHECKIN, {
        method: 'POST',
        credentials: 'include', // Importante para cookies de sessão
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': token || csrfToken, // Incluir token CSRF no header
        },
        body: JSON.stringify({
          idParticipante: selectedExternalId,
          tipo_checkin: "painel"
        }),
      });

      const data: CheckinResponse = await response.json();

      if (response.ok && data.success) {
        setResult("✅ Check-in painel realizado com sucesso!");
      } else {
        setResult(`❌ Erro: ${data.message || "Falha no check-in painel"}`);
      }
    } catch (err) {
      setResult("❌ Erro de conexão durante check-in painel");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="teste-caixa-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Carregando registrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="teste-caixa-container">
      <div className="teste-caixa-content">
        <h1>Teste Caixa - Check-in</h1>
        
        {error && (
          <div className="error-message">
            <p>❌ {error}</p>
          </div>
        )}

        <div className="form-section">
          <label htmlFor="participant-select">
            Selecionar Participante:
          </label>
          <select
            id="participant-select"
            value={selectedExternalId}
            onChange={(e) => setSelectedExternalId(e.target.value)}
            disabled={submitting}
          >
            <option value="">-- Selecione um participante --</option>
            {registrations.map((registration) => (
              <option key={registration.id} value={registration.external_id}>
                {registration.external_id} - {registration.name} ({registration.field_2 || '-'})
              </option>
            ))}
          </select>
        </div>

        <div className="button-section">
          <button
            onClick={handleSubmit}
            disabled={submitting || !selectedExternalId}
            className="submit-button"
          >
            {submitting ? "Enviando..." : "Enviar Check-in Caixa"}
          </button>
          <button
            onClick={handlePainelCheckin}
            disabled={submitting || !selectedExternalId}
            className="submit-button"
            style={{ marginLeft: 12 }}
          >
            {submitting ? "Enviando..." : "Enviar Painel"}
          </button>
        </div>

        {result && (
          <div className={`result-message ${result.includes('✅') ? 'success' : 'error'}`}>
            <p>{result}</p>
          </div>
        )}

        <div className="info-section">
          <p><strong>Total de registrations:</strong> {registrations.length}</p>
          {selectedExternalId && (
            <p><strong>ID selecionado:</strong> {selectedExternalId}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TesteCaixa;
